import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import instance from "../redux/actions/AxiosInstance/authenticated";
import smallLogo from "../assets/images/logo_small.png";

const ReferralModal = ({ isOpen, onClose, onSelectCreator }) => {
  const [referralSources, setReferralSources] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReferralSources();
    }
  }, [isOpen]);

  const fetchReferralSources = async () => {
    try {
      setIsLoading(true);
      const response = await instance.get(
        process.env.REACT_APP_BASE_URL + "/referral/sources"
      );
      
      if (response.data.status && response.data.data) {
        // Filter only active sources
        const activeSources = response.data.data.filter(
          source => source.status === "active"
        );
        setReferralSources(activeSources);
      }
    } catch (error) {
      console.error("Error fetching referral sources:", error);
      toast.error("Failed to load creators");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatorSelect = (creator) => {
    setSelectedCreator(creator);
  };

  const handleContinue = () => {
    if (selectedCreator) {
      // Save to localStorage
      localStorage.setItem("selectedReferralSource", JSON.stringify(selectedCreator));
      onSelectCreator(selectedCreator);
      onClose();
    }
  };

  const handleSkip = () => {
    // Save skip status to localStorage
    localStorage.setItem("referralModalSkipped", "true");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="p-4 pb-0 flex-shrink-0">
          <div className="flex items-center space-x-3">
              <img src={smallLogo} alt="logo" className="w-6 h-6" />
            <h2 className="text-xl font-bold text-gray-900">Scan Profit</h2>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mt-3">
            Whose Team Are You On?
          </h3>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {referralSources.map((creator) => (
                <div
                  key={creator.id}
                  className={`relative cursor-pointer rounded-lg border-2 transition-all duration-200 ${
                    selectedCreator?.id === creator.id
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleCreatorSelect(creator)}
                >
                  {/* Creator Image */}
                  <div className="aspect-square rounded-t-lg overflow-hidden">
                    {creator.image_url ? (
                      <img
                        src={creator.image_url}
                        alt={creator.display_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center">
                          <span className="text-2xl font-bold text-white">
                            {creator.display_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Creator Info */}
                  <div className="p-4">
                    <h4 className="font-medium text-gray-900 mb-3">
                      {creator.display_name}
                    </h4>
                    
                    {/* Join Button */}
                    <button
                      className={`w-full py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                        selectedCreator?.id === creator.id
                          ? "bg-green-500 text-white"
                          : "bg-green-500 text-white hover:bg-green-600"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreatorSelect(creator);
                      }}
                    >
                      {selectedCreator?.id === creator.id ? "Selected" : "Join ->"}
                    </button>
                  </div>

                  {/* Selection Indicator */}
                  {selectedCreator?.id === creator.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="flex justify-center p-4 flex-shrink-0 border-t border-gray-100">
          <div className="flex space-x-4">
            <button
              onClick={handleSkip}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
            >
              Skip
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedCreator}
              className={`px-8 py-2 rounded-full font-medium transition-colors duration-200 ${
                selectedCreator
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralModal;
